import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowRight, CheckCircle2, ClipboardCheck, Clock3, FileCheck2, KeyRound, LogOut, Radio, ShieldCheck, UserCheck, XCircle } from 'lucide-react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { api, clearCredentials } from '../lib/api'
import { FeishuOauthLink } from '../components/FeishuOauthLink'
import { FeishuLicenseIssuancePanel } from '../components/FeishuLicenseIssuancePanel'
import { ControlledEvidenceReview } from '../components/ControlledEvidenceReview'
import { FeishuOperationPasswordSetup } from '../components/FeishuOperationPasswordSetup'
import { StructuredActionDialog, type StructuredActionSubmission } from '../components/StructuredActionDialog'
import type { ApprovalEvidenceStatus, FeishuIntegrationStatus, FeishuTaskDetail, Session, ValidationResolutionStatus } from '../types'

const taskStatusLabel: Record<string, string> = {
  OPEN: '待领取',
  ASSIGNED: '已分配',
  CLAIMED: '已领取',
  COMPLETED: '已完成',
}

type RejectionKind = 'LICENSE'

const licenseRejectionCategories = [
  { value: 'CONTRACT_MISMATCH', label: '合同或订单信息不一致' },
  { value: 'ENTITLEMENT_SCOPE', label: '产品、Edition 或期限不符合' },
  { value: 'EVIDENCE_INSUFFICIENT', label: '审批证据不足' },
  { value: 'CUSTOMER_CHANGE', label: '客户需求已变更' },
  { value: 'OTHER', label: '其他原因' },
]


export function FeishuTaskDetailPage({ platform = false }: { platform?: boolean }) {
  const { taskId = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [session, setSession] = useState<Session>({ authenticated: false })
  const [status, setStatus] = useState<FeishuIntegrationStatus>()
  const [task, setTask] = useState<FeishuTaskDetail>()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [comment, setComment] = useState('')
  const [approvalEvidence, setApprovalEvidence] = useState<ApprovalEvidenceStatus>()
  const [specialConfirmed, setSpecialConfirmed] = useState(false)
  const [rejectionKind, setRejectionKind] = useState<RejectionKind>()
  const [validationNote, setValidationNote] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [current, currentStatus] = await Promise.all([
        api.session().catch(() => ({ authenticated: false } as Session)),
        api.feishuStatus(),
      ])
      setSession(current); setStatus(currentStatus)
      if (current.authenticated && !current.passwordChangeRequired && taskId) {
        const nextTask = await api.feishuTask(taskId)
        setTask(nextTask)
        if (nextTask.taskKind === 'LICENSE_APPROVAL' && nextTask.caseFile?.license
            && nextTask.availableActions.includes('APPROVE_LICENSE')) {
          setApprovalEvidence(await api.approvalEvidence(nextTask.caseFile.license.licenseId))
        }
      }
    } catch (value) {
      setError(value instanceof Error ? value.message : '飞书任务加载失败')
    } finally {
      setLoading(false)
    }
  }, [taskId])

  useEffect(() => { void load() }, [load])

  async function logout() {
    await api.logout().catch(() => undefined)
    clearCredentials(); navigate('/login', { replace: true })
  }

  async function act(action: () => Promise<unknown>) {
    setSaving(true); setError('')
    try { await action(); await load() }
    catch (value) { setError(value instanceof Error ? value.message : '任务处理失败') }
    finally { setSaving(false) }
  }

  const license = task?.caseFile?.license
  const licenseId = license?.licenseId || text(task?.payload.licenseId) || ''
  const canClaim = task?.availableActions.includes('CLAIM') && task.taskStatus !== 'COMPLETED' && !task.claimedAt
  const canApproveLicense = task?.availableActions.includes('APPROVE_LICENSE') && licenseId
  const canRejectLicense = task?.availableActions.includes('REJECT_LICENSE') && licenseId
  const canConfirm = task?.availableActions.includes('COMPLETE_CONFIRMATION')
  const validationException = task?.taskKind === 'VALIDATION_EXCEPTION' && task.taskStatus !== 'COMPLETED'
  const validationResolution = text(task?.payload.validationResolutionStatus) as ValidationResolutionStatus | ''
  const licenseApprovalTask = task?.taskKind === 'LICENSE_APPROVAL' && Boolean(license)
  const embeddedIssuance = !platform && task?.taskKind === 'LICENSE_ISSUANCE'
    && task.availableActions.includes('OPEN_ISSUANCE') && Boolean(task.caseFile)
  const externalDecisionTask = ['HIGH_RISK_OPERATION', 'COMMERCIAL_AUTHORIZATION_REVIEW', 'SIGNING_KEY_APPROVAL']
    .includes(task?.taskKind || '')
  const opensBusinessPage = !embeddedIssuance && (externalDecisionTask || Boolean(task?.availableActions.some(action => [
    'OPEN_ISSUANCE', 'OPEN_ACTIVATION', 'OPEN_APPLICATION',
  ].includes(action))))
  const decisionLabel = task?.availableActions.includes('OPEN_APPLICATION') ? '发起新的 License 申请' : ({
    HIGH_RISK_OPERATION: '进入高风险操作审批',
    COMMERCIAL_AUTHORIZATION_REVIEW: '进入商业授权复核',
    SIGNING_KEY_APPROVAL: '进入密钥轮换审批',
    LICENSE_ISSUANCE: '进入签发确认页',
    DELIVERY_ACTIVATION: '继续交付与激活',
    VALIDATION_EXCEPTION: '查看授权检查诊断与处理',
    OTHER: task?.eventType.includes('RENEWAL') || task?.eventType.includes('EXPIRING')
      || task?.eventType.includes('EXPIRED') || task?.eventType === 'LICENSE_APPLICATION_REJECTED'
      || task?.eventType === 'LICENSE_APPLICATION_RETURNED'
      ? '发起新的 License 申请' : '进入下一步',
  } as Record<string, string>)[task?.taskKind || '']
  const signInUrl = useMemo(() => {
    const returnTo = `${location.pathname}${location.search}`
    return `/api/integrations/feishu/login?returnTo=${encodeURIComponent(returnTo)}`
  }, [location.pathname, location.search])

  function approve(event: FormEvent) {
    event.preventDefault()
    if (!license || !comment.trim() || !approvalEvidence?.complete) return
    if (license.specialApprovalRequired && !specialConfirmed) return
    void act(() => api.approveLicense(licenseId, comment.trim(), specialConfirmed, true))
  }

  async function submitRejection(value: StructuredActionSubmission) {
    if (!task || !rejectionKind) throw new Error('拒绝任务上下文已失效，请关闭后刷新任务')
    const categories = licenseRejectionCategories
    const category = categories.find(item => item.value === value.category)?.label || '其他原因'
    const reason = `${category}：${value.reason.trim()}`
    setSaving(true); setError('')
    try {
      await api.rejectLicense(licenseId, reason)
      return { reference: licenseId, message: 'License 申请已拒绝，申请人可根据意见修正后重新提交。' }
    } finally {
      setSaving(false)
    }
  }

  function closeRejection() {
    setRejectionKind(undefined); setError('')
    void load()
  }

  if (loading) return <main className={platform ? 'platform-task-detail platform-task-detail--loading' : 'feishu-workbench feishu-workbench--loading'}><span className="feishu-loader" /><p>正在加载责任任务</p></main>
  if (!session.authenticated) return <main className="feishu-workbench"><section className="feishu-signin-card"><span><ShieldCheck size={26} /></span><small>FEISHU TASK</small><h1>进入具体 License 任务</h1><p>{status?.oauthReady ? '请先使用当前飞书身份登录，登录后会回到这张任务卡片对应的详情页。' : '飞书 OAuth 尚未完成启用、App ID、App Secret 和公网地址配置。'}</p>{error && <div className="error-banner">{error}</div>}<FeishuOauthLink className={`button button--primary ${status?.oauthReady ? '' : 'button--disabled'}`} href={status?.oauthReady ? signInUrl : undefined}>使用飞书身份进入<ArrowRight size={17} /></FeishuOauthLink><Link to="/login?source=feishu">返回账号密码登录</Link></section></main>
  if (session.passwordChangeRequired && session.authenticationMethod === 'FEISHU') {
    return <FeishuOperationPasswordSetup session={session} returnTo={location.pathname + location.search} />
  }
  if (session.passwordChangeRequired) return <main className="feishu-workbench"><section className="feishu-signin-card"><span><KeyRound size={26} /></span><small>LOCAL PASSWORD ONBOARDING</small><h1>请先完成内部密码设置</h1><p>当前本地登录会话仍使用临时密码。完成密码接管后再打开任务，避免在高风险操作时被阻断。</p><Link className="button button--primary" to="/">进入密码设置<ArrowRight size={17} /></Link><a href={signInUrl}>改用飞书验证</a></section></main>
  if (!task) return <main className={platform ? 'platform-task-detail' : 'feishu-workbench'}>{!platform && <FeishuTopbar onLogout={logout} />}<section className="feishu-task-detail"><div className="case-load-error"><h2>任务不可用</h2><p>{error || '没有找到对应的责任任务。'}</p><Link className="button button--primary" to={platform ? '/tasks' : '/feishu'}>返回个人任务箱</Link></div></section></main>

  return <main className={platform ? 'platform-task-detail' : 'feishu-workbench'}>
    {!platform && <FeishuTopbar onLogout={logout} />}
    <section className="feishu-task-detail">
      <Link className="case-back" to={platform ? '/tasks' : '/feishu'}>返回个人任务箱</Link>
      <header className="feishu-task-hero">
        <div><small>{platform ? 'AUTHORITY TASK' : 'FEISHU BUSINESS TASK'} · {task.stage}</small><h1>{task.title}</h1><p>{platform ? '这是与飞书共用状态的权威责任任务。岗位、责任人、职责分离和审计均由服务端统一执行。' : '你正在飞书内办理这项业务。审批、驳回和签发结果会直接写入同一权威流程，并同步更新 License Center 与原飞书卡片。'}</p></div>
        <aside><span className={`feishu-task-state feishu-task-state--${task.taskStatus.toLowerCase()}`}>{taskStatusLabel[task.taskStatus] || task.taskStatus}</span><b>{formatDate(task.deadlineAt) || '未设置截止时间'}</b><small>任务截止时间</small></aside>
      </header>
      {error && <div className="error-banner">{error}</div>}
      <div className="feishu-task-layout">
        <section className="feishu-task-main">
          <article className="feishu-task-card">
            <div className="panel-heading"><div><h3>业务对象</h3><p>License、客户、产品和当前流程上下文</p></div><ClipboardCheck size={19} /></div>
            <dl className="feishu-task-facts">
              <Fact label="License ID" value={licenseId} mono />
              <Fact label="客户" value={license?.customerName || text(task.payload.customerName)} />
              <Fact label="产品 / 版本" value={license ? `${license.productName} · ${license.edition}` : text(task.payload.productName)} />
              <Fact label="合同 / 订单" value={license?.contractNo || text(task.payload.contractNo)} />
              <Fact label="申请人" value={license?.requestedBy || text(task.payload.requestedBy)} />
              <Fact label="申请类型" value={license?.requestType || text(task.payload.requestType)} />
            </dl>
          </article>
          {licenseApprovalTask && license && <>
            <article className="feishu-task-card feishu-evidence-card">
              <div className="panel-heading"><div><h3>合同与期限证据</h3><p>来自申请引用的商业授权快照，不以卡片摘要代替</p></div><FileCheck2 size={19} /></div>
              <dl className="feishu-task-facts">
                <Fact label="商业授权 ID" value={license.commercialAuthorizationId} mono />
                <Fact label="合同 / 订单" value={license.contractNo} />
                <Fact label="项目" value={license.projectName} />
                <Fact label="授权有效期" value={`${license.startDate} 至 ${license.endDate}`} />
                <Fact label="Maintenance 截止" value={license.maintenanceEndDate} />
                <Fact label="宽限天数" value={`${license.graceDays} 天`} />
                <Fact label="部署类型" value={license.deploymentType} />
                <Fact label="计划安装实例" value={license.plannedInstallationId} mono />
                <Fact label="授权快照 SHA-256" value={license.commercialAuthorizationSnapshotSha256} mono />
                <Fact label="商业授权状态" value={task.caseFile?.commercialAuthorization?.status} />
                <Fact label="授权复核人" value={task.caseFile?.commercialAuthorization?.reviewedBy} />
                <Fact label="授权复核意见" value={task.caseFile?.commercialAuthorization?.reviewComment} />
              </dl>
              <ControlledEvidenceReview status={approvalEvidence} onChange={setApprovalEvidence} />
            </article>
            <article className="feishu-task-card feishu-evidence-card">
              <div className="panel-heading"><div><h3>完整 Feature 清单</h3><p>{license.featureSnapshot.length} 项权益；逐项核对名称、代码和依赖</p></div><ClipboardCheck size={19} /></div>
              <div className="feishu-feature-grid">{license.featureSnapshot.map(feature => <div key={feature.code}><b>{feature.name}</b><code>{feature.code}</code><small>{feature.category}{feature.requiredModuleCode ? ` · 依赖 ${feature.requiredModuleCode}` : ''}</small>{feature.description && <p>{feature.description}</p>}</div>)}</div>
            </article>
            <article className="feishu-task-card feishu-evidence-card">
              <div className="panel-heading"><div><h3>特殊说明与系统校验</h3><p>系统校验辅助判断，最终结论由当前责任人确认</p></div><AlertTriangle size={19} /></div>
              <div className="feishu-exception-note"><b>{license.specialApprovalRequired ? '需要特殊确认' : '标准申请'}</b><p>{license.exceptionReason || '申请人未填写特殊说明；当前申请未触发特殊审批条件。'}</p></div>
              <div className="feishu-validation-list">{license.validation.checks.map(check => <div key={check.code} className={`feishu-validation-item feishu-validation-item--${check.result.toLowerCase()}`}><span>{check.result}</span><b>{check.code}</b><p>{check.detail}</p></div>)}</div>
            </article>
          </>}
          {embeddedIssuance && task.caseFile && <FeishuLicenseIssuancePanel caseFile={task.caseFile} onReturned={load} />}
          <article className="feishu-task-card">
            <div className="panel-heading"><div><h3>你需要检查并完成</h3><p>按任务类型生成的处理清单</p></div><CheckCircle2 size={19} /></div>
            <Checklist value={task.checklist} />
          </article>
          <article className="feishu-task-card">
            <div className="panel-heading"><div><h3>职责分离与后果</h3><p>不处理会触发 SLA 升级或业务失效</p></div><ShieldCheck size={19} /></div>
            <div className="feishu-task-notes"><p><b>禁止操作：</b>{task.separationRule}</p><p><b>逾期结果：</b>{task.consequence}</p></div>
          </article>
          {task.caseFile && <article className="feishu-task-card">
            <div className="panel-heading"><div><h3>交付进度</h3><p>{task.caseFile.workflow.label}</p></div><Radio size={19} /></div>
            <ol className="feishu-task-timeline">{task.caseFile.timeline.map(item => <li key={item.code}><span className={`feishu-task-dot feishu-task-dot--${item.state.toLowerCase()}`} /><div><b>{item.label}</b><p>{item.detail}</p><small>{formatDate(item.occurredAt)} {item.actor ? `· ${item.actor}` : ''}</small></div></li>)}</ol>
          </article>}
          {task.signalTimeline.length > 0 && <article className="feishu-task-card">
            <div className="panel-heading"><div><h3>主任务信号时间线</h3><p>同一交付或异常阶段只保留一张待办卡片，以下记录阶段推进过程</p></div><Radio size={19} /></div>
            <ol className="feishu-task-timeline">{task.signalTimeline.map((item, index) => <li key={`${item.eventType}-${item.occurredAt}-${index}`}><span className="feishu-task-dot feishu-task-dot--done" /><div><b>{signalLabel(item.eventType)}</b><p>{item.detail}</p><small>{formatDate(item.occurredAt)}</small></div></li>)}</ol>
          </article>}
        </section>
        <aside className="feishu-task-side">
          <article className="feishu-task-card">
            <div className="panel-heading"><div><h3>为什么发给你</h3><p>责任人和可认领范围</p></div><UserCheck size={19} /></div>
            <dl className="feishu-task-facts feishu-task-facts--single">
              <Fact label="责任岗位" value={task.responsibleRole} />
              <Fact label="当前责任人" value={task.assignedTo || '待认领'} />
              <Fact label="可认领人员" value={task.claimableUsernames.join('、') || '按岗位开放'} />
              <Fact label="升级责任人" value={task.escalationTarget} />
            </dl>
            <p className="feishu-task-reason">{task.reason}</p>
          </article>
          <article className="feishu-task-card">
            <div className="panel-heading"><div><h3>任务状态</h3><p>已读、认领和办理结果</p></div><Clock3 size={19} /></div>
            <dl className="feishu-task-facts feishu-task-facts--single">
              <Fact label="已读" value={task.readAt ? `${task.readBy || ''} ${formatDate(task.readAt)}` : '尚未读取'} />
              <Fact label="认领" value={task.claimedAt ? `${task.claimedBy || ''} ${formatDate(task.claimedAt)}` : '尚未认领'} />
              <Fact label="办理" value={task.handledAt ? `${task.handledBy || ''} ${formatDate(task.handledAt)}` : '尚未办理'} />
              <Fact label="结果" value={task.handlingResult || '等待业务动作回写'} />
            </dl>
          </article>
          <article className="feishu-task-actions">
            {canClaim && <button className="button button--ghost" disabled={saving} onClick={() => void act(() => api.claimOutboxEvent(task.taskId))}>认领任务</button>}
            {canApproveLicense && license && <form className="feishu-approval-form" onSubmit={approve}>
              <div><b>审批确认</b><p>系统不代填审批意见。请根据上方证据，用一句话记录你的真实判断。</p></div>
              <label>审批意见<textarea value={comment} onChange={event => setComment(event.target.value)} required maxLength={500} /></label>
              <div className={`controlled-evidence-summary ${approvalEvidence?.complete ? 'is-complete' : ''}`}><FileCheck2 size={16} />{approvalEvidence?.complete ? '系统已确认当前审批人查看了全部必需原件。' : '请先查看全部必需合同原件。'}</div>
              {license.specialApprovalRequired && <label className="switch-row"><input type="checkbox" checked={specialConfirmed} onChange={event => setSpecialConfirmed(event.target.checked)} /><span><b>确认特殊风险</b><small>已核对特殊申请说明和客户影响，并接受审批通过后的风险。</small></span></label>}
              <button className="button button--primary" disabled={saving || !comment.trim() || !approvalEvidence?.complete || license.specialApprovalRequired && !specialConfirmed}><CheckCircle2 size={16} />确认审批通过</button>
            </form>}
            {canRejectLicense && <button className="button button--danger" disabled={saving} onClick={() => setRejectionKind('LICENSE')}><XCircle size={16} />拒绝申请</button>}
            {validationException && <div className="feishu-validation-resolution">
              <b>授权检查异常处理</b><p>{validationResolutionLabel(validationResolution)}。处理状态仅记录排查进度；必须待设备后续授权检查通过才会自动闭环。</p>
              <label>处理说明（可选）<textarea value={validationNote} onChange={event => setValidationNote(event.target.value)} maxLength={500} placeholder="例如：已核对设备身份，等待客户重新导入响应文件" /></label>
              <div><button className="button button--ghost" disabled={saving} onClick={() => void act(() => api.updateValidationResolution(task.taskId, 'INVESTIGATING', validationNote.trim()))}>开始排查</button><button className="button button--primary" disabled={saving} onClick={() => void act(() => api.updateValidationResolution(task.taskId, 'FIXED_PENDING_VALIDATION', validationNote.trim()))}>已修复，等待再次检查</button><button className="button button--ghost" disabled={saving} onClick={() => void act(() => api.updateValidationResolution(task.taskId, 'ESCALATED_TO_SUPPORT', validationNote.trim()))}>转交支持</button></div>
            </div>}
            {canConfirm && <button className="button button--primary" disabled={saving} onClick={() => void act(() => api.acknowledgeOutboxEvent(task.taskId))}>确认交付完成</button>}
            {opensBusinessPage && <Link className="button button--primary" to={task.primaryDestination}><KeyRound size={16} />{decisionLabel}</Link>}
            {licenseId && <Link className="button button--ghost" to={`/license-cases/${encodeURIComponent(licenseId)}`}><KeyRound size={16} />打开 License 交付单</Link>}
            {!licenseId && !opensBusinessPage && <Link className="button button--ghost" to={task.primaryDestination}><KeyRound size={16} />查看关联业务</Link>}
          </article>
        </aside>
      </div>
    </section>
    <StructuredActionDialog
      actionKey={`reject-${rejectionKind?.toLowerCase() || 'task'}-${task.taskId}`}
      open={Boolean(rejectionKind)}
      title="拒绝 License 申请"
      eyebrow="REJECTION REVIEW"
      description="请根据合同、授权范围和系统校验结果填写真实拒绝意见，不要仅填写“不通过”。"
      impact={[`客户：${license?.customerName || text(task.payload.customerName) || '未记录'}`, `License ID：${licenseId}`, `产品 / 版本：${license ? `${license.productName} / ${license.edition}` : text(task.payload.productName) || '未记录'}`, '拒绝后本次申请结束，不会进入签发。', '申请人需根据意见修正后重新提交。']}
      categories={licenseRejectionCategories}
      checklist={['已核对客户、License、合同和授权范围。', '已确认拒绝后本次申请不再进入签发。', '已填写可供申请人修正的具体意见。']}
      reasonMaxLength={450}
      submittedTitle="拒绝结论已记录"
      submitLabel="确认拒绝"
      confirmationHint="提交后将立即记录拒绝结论并关闭本次任务，不能在当前任务中直接撤回。"
      saving={saving}
      error={error}
      onClose={closeRejection}
      onSubmit={submitRejection}
    />
  </main>
}

function FeishuTopbar({ onLogout }: { onLogout: () => void }) {
  return <header className="feishu-topbar"><div className="feishu-wordmark"><span><ShieldCheck size={18} /></span><b>License Center</b><small>飞书任务</small></div><button onClick={() => void onLogout()}><LogOut size={16} />退出</button></header>
}

function Fact({ label, value, mono }: { label: string, value?: string, mono?: boolean }) {
  return <div><dt>{label}</dt><dd className={mono ? 'mono' : undefined}>{value || '—'}</dd></div>
}

function Checklist({ value }: { value: string }) {
  return <ol className="feishu-task-checklist">{value.split('\n').filter(Boolean).map((item, index) => <li key={index}>{item.replace(/^\d+\.\s*/, '')}</li>)}</ol>
}

function formatDate(value?: string) {
  if (!value) return ''
  return new Date(value).toLocaleString('zh-CN', { hour12: false })
}

function text(value: unknown) {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function signalLabel(value: string) {
  const labels: Record<string, string> = {
    LICENSE_ISSUED: 'License 已签发', LICENSE_DOWNLOAD_AVAILABLE: 'Artifact 可下载', LICENSE_DOWNLOADED: 'Artifact 已下载',
    LICENSE_ACTIVATION_REQUIRED: '等待激活', OFFLINE_ACTIVATION_REQUEST_RECEIVED: '已收到离线请求',
    OFFLINE_ACTIVATION_RESPONSE_AVAILABLE: '离线响应可下载', OFFLINE_ACTIVATION_RECEIPT_REQUIRED: '等待设备回执',
    LICENSE_ACTIVATION_COMPLETED: '激活已完成', LICENSE_VALIDATION_DENIED: '设备授权检查被拒绝',
    LICENSE_VALIDATION_RESOLUTION_UPDATED: '授权检查异常处理已更新',
  }
  return labels[value] || value
}

function validationResolutionLabel(value: ValidationResolutionStatus | '') {
  return ({
    INVESTIGATING: '排查中',
    FIXED_PENDING_VALIDATION: '已修复，等待再次检查',
    ESCALATED_TO_SUPPORT: '已转交支持',
  } as Record<string, string>)[value] || '待处理'
}
